
import { SimulationConfig, ShapeType, ParallaxEffectType, BaseBackgroundEffectType } from '@/types';

export const STORAGE_KEY = 'hypnotic_ball_config_v25';
export const GLOBAL_USER_SOUNDS_KEY = 'hypnotic_global_user_sounds';
export const GLOBAL_CUSTOM_MELODIES_KEY = 'hypnotic_global_custom_melodies';
export const MS_PER_TICK = 1000 / 60;

export const SHAPES: ShapeType[] = [
  'TRIANGLE', 'SQUARE', 'PENTAGON', 'HEXAGON', 'HEPTAGON', 'OCTAGON', 
  'NONAGON', 'DECAGON', 'CIRCLE', 'STAR_5', 'STAR_6', 'STAR_8', 
  'HEART', 'DIAMOND', 'CROSS', 'CLOVER', 'FLOWER', 'SHIELD', 
  'INVERTED_TRIANGLE', 'GEM', 'GEAR'
];

export const BALL_SOUND_EFFECTS: string[] = [
  'Pop', 'Wood', 'Metal', 'Synth-Zap', 'Crystal', 'Soft-Thud', 'Explosion', 
  'Laser-Gun', 'Pistol', 'Sniper', 'Glass-Break', 'Digital-Beep', 'Retro-Jump', 
  'Thunder', 'Bubble', 'Camera', 'Sword', 'Robot'
];

export const DEFAULT_CONFIG: SimulationConfig = {
  mode: 'SPAWNER',
  
  // FEATURE FLAGS (Unified Engine)
  enableRhythmic: false,
  enableEscape: false,
  enableSpawner: true,
  enableDrawer: false,
  enableSpaceSpiral: false,
  enableTrails: false,

  // CONSOLIDATED BACKGROUND SYSTEM
  backgroundMode: 'LIGHT',
  
  gravity: 0.0375, restitution: 1, maxBalls: 100, initialBalls: 1,
  containerRadius: 200, containerLineWidth: 4, ballRadius: 10, ballMass: 1, spawnOnWallCollision: false,
  spawnOnBallCollision: false,
  rainbowMode: true,
  titleFontSize: 24,
  
  // ADVANCED SPAWNER PROTOCOL DEFAULTS
  spawnerEngineMode: 'BASIC',
  spawnerPattern: 'BURST',
  spawnerBurstCount: 4,
  spawnerSpawnInterval: 2000,
  spawnerVelocityInheritance: 0.5,
  spawnerVelocityRandomization: 0.3,
  spawnerVelocitySpread: 45,
  
  // FISSION DEFAULTS
  fissionEnabled: false,
  fissionMaxDepth: 3,
  fissionSplitCount: 2,
  fissionSizeMultiplier: 0.7,
  fissionVelocityBoost: 1.2,
  fissionMinRadius: 2,
  fissionAngleOffset: 30,
  
  // FUSION DEFAULTS
  fusionEnabled: false,
  fusionMaxRadius: 100,
  fusionMassConservation: true,
  
  // BALL LIFECYCLE DEFAULTS
  ballLifespanEnabled: false,
  ballLifespan: 5.0,
  ballFadeOutPower: 1.0,
  ballShrinkOnDeath: true,
  
  // AUDIO REACTIVE SPAWNING DEFAULTS
  spawnOnAudioPeak: false,
  spawnAudioThreshold: 0.8,
  spawnAudioFrequencyRange: [20, 20000],
  
  titleOffsetY: 30, titleBold: true,
  numOuterBorders: 1, rgbLighting: true, 
  
  // SHAPE DEFAULTS
  containerShape: 'CIRCLE',
  initialContainerShape: 'CIRCLE',
  containerRotation: 0,
  ballShape: 'CIRCLE',
  ballRotationMode: 'NONE',
  shapeMorphEnabled: false,
  shapeMorphSpeed: 1.0,
  shapeMorphDuration: 1000,
  shapeMorphContinuous: false,
  morphableShapes: [
      'CIRCLE', 'SQUARE', 'TRIANGLE', 'PENTAGON', 'HEXAGON', 'HEPTAGON', 'OCTAGON', 
      'NONAGON', 'DECAGON', 'STAR_5', 'STAR_6', 'STAR_8', 'HEART', 'DIAMOND', 
      'CROSS', 'CLOVER', 'FLOWER', 'SHIELD', 'INVERTED_TRIANGLE', 'GEM', 'GEAR'
  ],

  // GRANULAR VISUAL OVERRIDES
  useBallRGB: true,
  useTrailRGB: true,
  useContainerRGB: true,
  useFXRGB: true,
  useHUDRGB: true,
  
  renderOrder: ['WALLS', 'RINGS', 'TRAIL', 'BLAST', 'FX', 'BALL', 'SPECTRUM', 'HUD'],

  trailRGB: true, trailEnabled: false, trailLength: 50,
  trailPersistence: false, // Default off
  titleText: "",
  titleFontFamily: "Inter",
  titleFontWeight: "900",
  darkMode: false, selectedMelody: 'Megalovania',
  growthRate: 0.05,
  trailInterval: 20,
  trailOpacity: 0.6,
  trailWidthMultiplier: 1.0,
  trailStartScale: 1.0,
  trailEndScale: 0.1,
  trailMatchBallSize: true,
  trailGlowAmount: 0,
  trailCustomColor: '#ffffff',
  
  // NEW TRAIL FEATURES
  trailStrokeColor: '#000000',
  trailStrokeWidth: 2, // Default visible black border
  trailApplyBorderToPersistence: false,
  trailOcclusionOptimization: false,
  trailDistanceBased: true,
  trailDistanceInterval: 10,
  
  // DYNAMICS
  speedIncreaseMode: 'STATIC',
  speedIncreaseRate: 0.005,
  speedAcceleration: 0,
  initialSpeed: 1.0,
  masterSpeed: 1.0,

  ballGrowthMode: 'NONE',
  ballGrowthRate: 0.5,
  ballGrowthAcceleration: 0,
  ballShrinkMode: 'NONE',
  ballShrinkRate: 0.5,
  ballMaxRadius: 100,
  ballMinRadius: 10,
  ballRotationSpeed: 1.0,
  
  ballWeightGrowthEnabled: false,
  ballMinWeight: 1,
  ballMaxWeight: 10,
  ballWeightGrowthTime: 10,
  
  containerShrinkMode: 'NONE', // NEW
  containerShrinkRate: 0.5, // NEW
  containerShrinkAcceleration: 0,
  minContainerRadius: 50, // NEW

  // RHYTHMIC DEFAULTS
  rhythmicTimeScale: 1.0,
  rhythmicTimeScaleIncreaseMode: 'NONE',
  rhythmicTimeScaleRate: 0.01,
  rhythmicTimeScaleAcceleration: 0,
  rhythmicCircleScale: 1.0,
  rhythmicSyncMode: 'DIRECTION_ONLY',
  rhythmicMaxSizeSound: 'Explosion',
  rhythmicMaxSizeSoundType: 'SOUND_EFFECT',
  rhythmicMaxSizeCustomSoundId: '',
  rhythmicOuterLayerAnimation: 'EXPLOSION',

  // INDIVIDUAL COLORS
  ballColor: '#3b82f6',
  ball1Color: '#3b82f6', 
  ball2Color: '#3b82f6', 
  borderColor: '#1a1a1a',
  innerColor: '#ffffff',
  outerColor: '#ffffff',
  titleColor: '#171717',
  collisionWaveColor: '#3b82f6',
  blastColor: '#ef4444',
  blastMatchRingColor: false,
  
  footerText: "",
  footerTextColor: '#171717',
  footerFontSize: 20,
  footerOffsetY: 30,
  footerBold: false,
  footerFontFamily: "Inter",
  footerFontWeight: "500",
  audioTrailEffect: false,
  playlist: [],
  playlistEnabled: false,
  playlistLoop: true,
  playlistCurrentIndex: 0,
  playlistTransitionTime: 1000,
  rhythmicPlaylistContinueSimulation: false,
  audioEcho: true,
  ballCollisionSoundType: 'MELODY',
  selectedBallSoundEffect: 'Pop',
  breakSoundType: 'SOUND_EFFECT',
  selectedBreakSoundEffect: 'Explosion',
  drawerReverseMode: false,
  drawerMinRadius: 15,
  drawerBrushType: 'SOLID',
  drawerSymmetryMode: 'NONE',
  drawerSymmetryCount: 4,
  drawerLineDecay: false,
  drawerLineLifetime: 5,
  drawerAutoClearInterval: 0,
  drawerColorMode: 'BALL',
  drawerFixedColor: '#ffffff',
  drawerGlowIntensity: 10,
  drawerVelocityScale: 5,
  drawerPointFrequency: 1.0,
  drawerMaxLength: 1000,
  drawerRainbowSpeed: 0.1,
  drawerAttractionMode: 'NONE',
  drawerAttractionStrength: 0.05,
  drawerAttractionRadius: 100,
  escapeRingCount: 100, 
  escapeGapSize: 30,
  escapeRotationSpeed: 1.5,
  escapeGapAlignment: 'ALIGNED',
  escapeShrinkMode: true,
  escapeStaticMode: false,
  escapeShrinkRate: 0.5,
  escapeRotationMode: 'CLOCKWISE',
  escapeRingSpacing: 40,
  escapeStopRotationOnHit: false,
  escapeDynamicShrink: false,
  escapeMinRadius: 200, 
  escapeAutoDisableBordersEnabled: false,
  escapeAutoDisableBordersBounces: 10,
  escapeBreakAnimation: 'SHATTER',
  escapeShowOuterBorders: false,
  ringCounterStyle: 'CENTER',
  ringCounterFontSize: 30,
  escapeTimerFontSize: 220,
  timerColor: '#1a1a1a',
  escapeFixedGapDirection: 90,
  
  // Ball Placement Defaults
  ballPlacementMode: 'CENTER',
  ballPlacementPosX: 0.5, // 0-1 range (relative to screen)
  ballPlacementPosY: 0.5,
  ballPlacementBorder: 'TOP',
  ballPlacementAngle: 0,
  ballPlacementDistance: 200,
  ballPlacementDirectionAngle: 45,
  ballPlacementRandomDirection: true,
  ballPlacementIndividualEnabled: false,
  ballPlacementConfigs: [],

  escapeAccelerationEnabled: false,
  escapeAccelerationMultiplier: 0.1,
  escapeAccelerationDecay: 0.02,
  escapeAccelerationMax: 3.0,
  
  escapeOppositeGapCount: 0,
  escapeOppositeGapSize: 30,
  ringCounterReverse: false,

  // HUD DEFAULTS
  ringCounterFont: 'Brush Script MT, cursive',
  ringCounterFontWeight: '900',
  ringCounterColor: '#1a1a1a',
  ringCounterStrokeColor: '#000000',
  ringCounterStrokeWidth: 0,
  ringCounterOpacity: 0.4,
  ringCounterUseRGB: true,
  ringCounterUseRGBBorder: false, // DEFAULT OFF SO USER CAN EDIT COLOR
  ringCounterShadowBlur: 0,
  ringCounterShadowColor: '#000000',

  ballStyle: 'SHINY',
  escapeThrowAngle: 270,
  escapeThrowPower: 22,
  escapeTimeChallengeMode: false,
  escapeTimerStart: 10,
  escapeTimeAddition: 5,
  escapeTimeAdditionStep: -0.1,
  audioCropStart: 0,
  audioCropEnd: 100,
  customMelodies: [],
  fxPresets: [],
  userSounds: [],

  // BREAK SOUND ZONES
  enableBreakSoundZones: false,
  breakSoundZones: [],

  // CAMERA DEFAULTS
  cameraFollow: false,
  cameraSmoothness: 0.08,
  cameraLookahead: 5.0,
  cameraDeadzone: 20.0,
  cameraZoom: 1.0,
  cameraZoomSmoothness: 0.05,
  cameraPredictiveLookahead: true,
  cameraDamping: 0.15,
  cameraElasticity: 0.1,

  // HUD
  ringCounterPosition: 'FOLLOW_BALL',
  ringCounterOffset: -40,

  showBallSizeCounter: false,
  ballSizeCounterPosition: 'BOTTOM_CENTER',
  ballSizeCounterOffset: 40,
  ballSizeCounterPrefix: "Ball Size: ",
  ballSizeCounterSuffix: "px",
  ballSizeCounterColor: '#1a1a1a',
  ballSizeCounterFontSize: 24,
  ballSizeCounterFont: 'Inter, sans-serif',
  ballSizeCounterFontWeight: '600',
  ballSizeCounterOpacity: 0.8,
  ballSizeCounterUseRGB: false,
  ballSizeCounterShadowBlur: 4,
  ballSizeCounterShadowColor: '#000000',
  ballSizeCounterDecimalPlaces: 1,

  showBounceCounter: false,
  bounceCounterPosition: 'TOP_CENTER',
  bounceCounterOffset: 60,
  bounceCounterPrefix: '',
  bounceCounterSuffix: ' BOUNCES',
  bounceCounterColor: '#1a1a1a',
  bounceCounterFontSize: 14,
  bounceCounterFont: 'Inter, sans-serif',
  bounceCounterFontWeight: '900',
  bounceCounterOpacity: 1.0,
  bounceCounterUseRGB: false,
  bounceCounterShadowBlur: 0,
  bounceCounterShadowColor: 'rgba(0,0,0,0.5)',

  // COLLISION EFFECTS (IMPACT FX)
  collisionEffect: 'GRAVITY_PARTICLES',
  collisionEffectDuration: 800,
  collisionEffectParticleCount: 20,
  collisionEffectGravity: 0.5,
  collisionEffectScale: 1.0,
  collisionEffectColorMode: 'BALL_COLOR',
  collisionEffectColor1: '#ff0000',
  collisionEffectColor2: '#ffff00',

  // ADVANCED DEFAULTS
  physicsSubsteps: 4,
  airResistance: 1.0,
  hueCycleSpeed: 0.8,
  shakeIntensityMultiplier: 1.0,
  shakeDecayRate: 0.92,
  popDecayRate: 0.05,
  escapeRingAlphaBase: 1.0,
  escapeRingAlphaFade: 1.0,
  escapeRingThickness: 4,
  escapeSideGuards: false,
  escapeSideGuardsVisible: true,
  escapeSideGuardColor: '#ffffff',
  escapeSideGuardThickness: 5,
  
  escapeGapSupportEnabled: false,
  escapeGapSupportMinRadius: 50,

  escapeMaxVisibleRings: 450,
  maxVelocityLimit: 80,
  minVelocityLimit: 0.1,
  antiStallMultiplier: 1.0,
  drawerLineThickness: 1,
  drawerLineOpacity: 0.2,
  blastDuration: 4000, 
  collisionVolume: 0.5,
  melodyVolume: 0.6,
  masterVolume: 1.0,
  fxVolume: 0.8,

  // MELODY RACK DEFAULTS
  melodyInstrument: 'GRAND_PIANO',
  melodyWaveform: 'AUTO',
  audioReverbLevel: 0.15,
  audioDistortionLevel: 0,
  audioLowPassFreq: 12000,
  audioLowPassResonance: 1.0,
  audioDelayTime: 0.35,
  audioDelayFeedback: 0.45,
  audioEchoWet: 0.5,
  audioEchoStereoWidth: 0.7,
  
  // FX RACK DEFAULTS
  fxReverbLevel: 0.1,
  fxDistortionLevel: 0,
  fxLowPassFreq: 20000,
  fxLowPassResonance: 1.0,
  fxEcho: true,
  fxDelayTime: 0.2,
  fxDelayFeedback: 0.3,
  fxEchoWet: 0.4,
  
  audioCompoundMode: false,

  // SHARED AUDIO DEFAULTS
  audioHighPassFreq: 20,
  audioBitcrushDepth: 16,
  audioPitchShift: 0,
  audioChorus: false,
  audioChorusRate: 1.5,
  audioChorusDepth: 0.002,

  rhythmicBreakEffect: 'SHATTER',
  rhythmicBreakDuration: 4000,
  rhythmicBreakResetDelay: 4000,
  rhythmicBreakPieceCount: 20,
  rhythmicBreakGravity: 0.5,
  rhythmicBreakBlastRadius: 500,
  rhythmicBreakBlastDuration: 2000,
  rhythmicSyncOffset: 0,
  rhythmicDecoupleSpeed: true,
  rhythmicMinBounceDistance: 100,
  rhythmicCurveDepth: 0.3,
  rhythmicCurveSpeedProfile: 0.5,
  rhythmicMinSpeed: 0,
  rhythmicMaxSpeed: 100,
  rhythmicBallSpeedScale: 1.0,
  rhythmicBallSpeedDecoupled: true, // Default to true as per user request to decouple

  audioFlanger: false,
  audioFlangerRate: 0.25,
  audioFlangerDepth: 0.004,
  audioPhaser: false,
  audioPhaserRate: 0.5,
  audioPhaserDepth: 0.5,
  audioTremolo: false,
  audioTremoloRate: 5.0,
  audioTremoloDepth: 0.5,
  audioVibrato: false,
  audioVibratoRate: 5.0,
  audioVibratoDepth: 0.5,
  audioCompressor: true,
  audioCompThreshold: -12,
  audioCompRatio: 4,
  audioReverbPreDelay: 0.01,
  audioBitcrushFreq: 44100,
  escapeSpiralTightness: 0.15,

  // SLIDING AUDIO DEFAULTS
  audioSlidingEnabled: true,
  audioSlidingMaxSpeed: 50,
  audioSlidingVolume: 0.3,
  audioSlidingPitch: 1.0,
  
  // RING COLOR SETTINGS
  escapeColorMode: 'RAINBOW',
  escapeUniformColor: '#3b82f6',
  escapeColorA: '#ff0000',
  escapeColorB: '#ffffff',
  escapeRainbowSpeed: 1.0,
  escapeIndividualColors: [],

  // SPHERE STUDIO DEFAULTS
  ballFillType: 'SOLID',
  ballFillOpacity: 1.0,
  ballGradientInner: '#ffffff',
  ballGradientOuter: '#000000',
  ballStrokeColor: '#ffffff',
  ballStrokeWidth: 0,
  ballStrokeOpacity: 1.0,
  trailStrokeOpacity: 1.0,
  ballGlowRadius: 0,
  ballGlowColor: '#ffffff',
  ballGlowOpacity: 0.5,
  ballTexture: 'NONE',
  ballTextureOpacity: 0.2,
  ballSquashStretch: false,
  ballSquashFactor: 0.5,
  ballPulseSpeed: 0,

  // SONG SLICE MODE
  songSliceDuration: 0.15,
  songSliceContinuousMode: true,
  songSliceSequence: [],
  songTotalDuration: 0,
  songSpeedIncreaseEnabled: false,
  songSpeedMax: 2.0,
  songResetTimer: 1.0,
  songResetEnabled: false,
  audioCutoffEnabled: false,
  audioMutedInSlicer: false,

  // SHATTER FX
  shatterForce: 100,
  shatterGravity: 500,

  // PARALLAX WATERMARK DEFAULTS
  parallaxWatermarkText: "HYPNOTIC",
  parallaxWatermarkOpacity: 0.1,
  parallaxWatermarkFontSize: 40,
  parallaxWatermarkFontFamily: "Inter",
  parallaxWatermarkFontWeight: "900",
  parallaxWatermarkColor: "#ffffff",
  parallaxWatermarkRepeatX: 5,
  parallaxWatermarkRepeatY: 5,
  parallaxWatermarkAngle: -45,
  parallaxWatermarkSpeed: 0.5,
  parallaxWatermarkDensity: 0.5,
  parallaxWatermarkShadowColor: "rgba(0,0,0,0.5)",
  parallaxWatermarkShadowBlur: 0,
  parallaxWatermarkShadowOffsetX: 0,
  parallaxWatermarkShadowOffsetY: 0,
  parallaxWatermarkUseBallColor: false,

  // PARALLAX MATERIAL DEFAULTS
  parallaxImageUrl: 'https://picsum.photos/seed/space/1920/1080',
  parallaxVideoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-stars-in-the-night-sky-slow-motion-2798-large.mp4',
  parallaxMediaFit: 'COVER',
  parallaxMediaOpacity: 1.0,

  // BASE BACKGROUND SYSTEM DEFAULTS
  backgroundEnabled: true,
  backgroundEffect: BaseBackgroundEffectType.SOLID,
  backgroundPrimaryColor: '#ffffff',
  backgroundSecondaryColor: '#fafafa',
  backgroundTertiaryColor: '#f4f4f5',
  backgroundSpeed: 0.8,
  backgroundOpacity: 1.0,
  backgroundComplexity: 2.0,
  backgroundBrightness: 1.1,
  backgroundContrast: 1.2,
  backgroundSaturation: 1.3,
  backgroundAnimate: true,
  backgroundRainbowMode: false,
  backgroundRainbowSpeed: 1.0,
  backgroundMeshPoints: 6,
  backgroundNoiseScale: 1.0,
  backgroundTileSize: 100,
  backgroundAngle: 45,
  backgroundImageUrl: '',
  backgroundVideoUrl: '',
  backgroundMediaFit: 'COVER',
  backgroundMediaOpacity: 1.0,

  // OPTIMIZATION DEFAULTS
  dustResolution: 0.5,
  maxDustParticles: 100,
  maxActiveBlasts: 200,

  // ESCAPE ZOOM
  escapeZoomEffectEnabled: false,
  escapeZoomOutMax: 0.5,
  escapeZoomOutSpeed: 0.05,

  // HYPER SPEED / OPTIMIZATION
  enableHyperSpeed: false,
  hyperSpeedBatchSize: 50,
  hyperSpeedRenderLimit: 100,
  hyperSpeedSimplifiedVisuals: true,
  hyperSpeedAutoTune: true,
  hyperSpeedTargetTime: 10.0,
  hyperSpeedAcceleration: 0.1,
  hyperSpeedTriggerThreshold: 0.8,
  hyperSpeedStopShrink: true,

  // OPTIMIZATION SETTINGS
  enableFrustumCulling: true,
  frustumCullingMargin: 100,
  enableBlastCulling: true,
  cullRingsOutsideScreen: true,
  cullBlastsOutsideScreen: true,
  partialRingBlastOptimization: true,

  // SPACE SPIRAL
  spaceSpiralMassBox: 50,
  spaceSpiralMassBall: 1,
  spaceSpiralGravity: true,
  spaceSpiralSpeed: 5,
  spaceSpiralBoxCount: 100,
  spaceSpiralBoxSize: 30,
  spaceSpiralBoxWidth: 30,
  spaceSpiralBoxHeight: 30,
  spaceSpiralScale: 1.0,
  spaceSpiralBoxShape: 'SQUARE',
  spaceSpiralBoxColor: '#3b82f6',
  spaceSpiralRepeats: 1,
  spaceSpiralSoundSpawn: 'Pop',
  spaceSpiralSoundBounce: 'Wood',
  spaceSpiralBoxSpacing: 30,
  spaceSpiralMinBoxScale: 0.3,
  spaceSpiralMaxBoxScale: 1.0,
  spaceSpiralRotation: 0,
  spaceSpiralBallOffsetX: 0,
  spaceSpiralBallOffsetY: 0,
  spaceSpiralColorMode: 'RAINBOW',
  spaceSpiralColorA: '#3b82f6',
  spaceSpiralColorB: '#ef4444',
  spaceSpiralGradientSpeed: 1.0,
  spaceSpiralBoxBorderWidth: 0,
  spaceSpiralBoxBorderColor: '#ffffff',
  spaceSpiralBoxRotation: 0,
  spaceSpiralDebrisMode: true,
  spaceSpiralGravityPower: 1.0,
  spaceSpiralForwardThrust: 0,
  spaceSpiralThrustAngle: 0,
  spaceSpiralThrustDuration: 0,
  spaceSpiralRestitution: 0.5,
  spaceSpiralBoxRestitution: 1.0,
  spaceSpiralAirResistance: 1.0,
  spaceSpiralBoxAirResistance: 1.0,
  spaceSpiralBoxStyle: 'MATTE',
  spaceSpiralBoxTexture: 'NONE',
  spaceSpiralBoxTextureOpacity: 0.5,
  spaceSpiralBoxGlowRadius: 0,
  spaceSpiralBoxGlowColor: '#ffffff',
  spaceSpiralBoxGlowOpacity: 0.5,
  spaceSpiralBoxFillOpacity: 1.0,
  spaceSpiralEnableRounds: false,
  spaceSpiralRoundsPerSession: 1,
  spaceSpiralRoundTimeLimit: 60,
  spaceSpiralRoundConfigs: [],
  spaceSpiralThrowAngle: 0,
  spaceSpiralThrowPower: 20,
  
  // RHYTHMIC ESCAPE DEFAULTS
  rhythmicEscapeShieldRing: true,
  rhythmicEscapeShieldRingRadius: 100,
  rhythmicEscapeShieldRingDestroyMode: 'BOUNCES',
  rhythmicEscapeShieldRingDestroyValue: 20,
  rhythmicEscapeShieldBreakAnimation: 'SUPERNOVA',
  rhythmicEscapeAutoSync: true,
  rhythmicUseCustomTempo: false,

  // Outside Collider
  outsideColliderEnabled: false,
  outsideColliderWidth: 1080,
  outsideColliderHeight: 1920,
  outsideColliderScale: 1.0,
  outsideColliderRestitution: 1.0,
  outsideColliderOffsetX: 0,
  outsideColliderOffsetY: 0,
  outsideColliderCulling: false,
  outsideColliderCullingMargin: 50,

  // PARALLAX DEFAULTS
  parallaxEnabled: false,
  parallaxEffect: ParallaxEffectType.STARRY_NIGHT,
  parallaxIntensity: 0.5,
  parallaxSpeed: 1.0,
  parallaxLayers: 3,
  parallaxColorMode: 'THEME',
  parallaxCustomColor: '#ffffff',
  parallaxOpacity: 0.5,
  parallaxZoomInfluence: 0.5,
  parallaxBlur: 0,
  parallaxMotionBlur: 0,
  parallaxRotation: 0,
  parallaxScale: 1.0,
  parallaxComplexity: 1.0,
  parallaxBrightness: 1.0,
  parallaxContrast: 1.0,
  parallaxSaturation: 1.0,
  parallaxAutoScrollX: 0,
  parallaxAutoScrollY: 0,
  parallaxPatternDensity: 1.0,
  parallaxGlowEnabled: false,
  parallaxGlowRadius: 20,
  parallaxGlowOpacity: 0.5,

  // Trail Start Defaults
  trailStartPosX: 0,
  trailStartPosY: 0,
  trailStartAngle: 0,
  trailStartSpeed: 10,
  trailEnableBorderBreak: false,

  // Rhythmic MIDI Filtering
  rhythmicMidiFilterEnabled: false,
  rhythmicMidiFilteredTracks: [],
  rhythmicMidiBackgroundTracks: [],
  rhythmicMidiMutedTracks: [],
  rhythmicMidiPlayBackground: false,
  rhythmicMidiUseExactDuration: false,
  rhythmicPulseActive: false,
  rhythmicGlowActive: false,
  rhythmicSensitivity: 1.0,
  rhythmicBreakThreshold: 0.5,
  rhythmicVolume: 1.0,
  rhythmicMidiMinVelocity: 0,
  rhythmicMidiMaxVelocity: 127,
  rhythmicMidiMinPitch: 0,
  rhythmicMidiMaxPitch: 127,
  rhythmicMidiMinDuration: 0,
  rhythmicMidiMaxDuration: 10000,
  rhythmicMidiMuteBass: false,
  rhythmicMidiMuteMain: false,
  rhythmicMultiSimEnabled: false,
  rhythmicMultiSimIntroMode: 'SEQUENTIAL',
  rhythmicSimulations: [],

  rhythmicSpectrumEnabled: false,
  rhythmicSpectrumBarCount: 128,
  rhythmicSpectrumMaxHeight: 80,
  rhythmicSpectrumMinHeight: 0,
  rhythmicSpectrumBarWidth: 2,
  rhythmicSpectrumBarSpacing: 1,
  rhythmicSpectrumDuration: 1000,
  rhythmicSpectrumColorMode: 'RAINBOW',
  rhythmicSpectrumCustomColor: '#ffffff',
  rhythmicSpectrumGrowRate: 0.3,
  rhythmicSpectrumShrinkRate: 0.05,
  rhythmicSpectrumAudioSensitivity: 0.5,
  rhythmicSpectrumCollisionIntensity: 1.0,
  rhythmicSpectrumCollisionSpread: 12,
  rhythmicSpectrumAudioSpatiality: 0.0,
  rhythmicSpectrumGlobalAudio: false,

  // HUD SETTINGS
  showHUD: false,
  showDiagnosticsOverlay: false,
  hudPosition: 'TOP_LEFT',
  hudOpacity: 0.8,
  hudScale: 1.0,
  hudShowElapsedTime: false,
  hudShowSpeed: false,
  hudShowBallCount: false,
  hudShowBounceCount: false,
  hudShowRingsLeft: false,
  hudShowTimeRemaining: false,
  hudShowFPS: false,
  hudShowCurrentRound: false,
  hudShowContainerRadius: false,
  hudShowBallRadius: false,
  hudShowGravity: false,
  hudShowRestitution: false,
  showControlIsland: true,
  showStatusBadge: false,
  pinnedSidebar: false,
  pinnedPresets: [],

  // Universal Audio Overrides
  universalNoStereoEcho: false,
  universalNoReverb: false,
  universalNoDistortion: false,
  universalForceInstrument: false,
  universalMasterPitchShift: 0,
  universalLowPassOverride: false,
  universalLowPassFreq: 20000,
  
  // RECORDER DEFAULTS
  recorderResolution: '720p',
  recorderFPS: 60,
  recorderFormat: 'webm',
  recorderBitrate: 8000000,
  recorderAspect: '16:9',
  recorderWidth: 1280,
  recorderHeight: 720,
  recorderHideUI: true,
  recorderShowWatermark: true,
  recorderRegionEnabled: false,
  recorderRegionX: 0,
  recorderRegionY: 0,
  recorderRegionWidth: 1920,
  recorderRegionHeight: 1080,
  hudShowBallMass: false,
  hudShowTimeScale: false,
  hudShowInitialSpeed: false,
  hudShowSpeedIncreaseRate: false,
  hudShowAirResistance: false,
  hudShowPhysicsSubsteps: false,
  hudShowBallGrowthRate: false,
  hudShowContainerShrinkRate: false,
  recorderQualityMode: 'VBR',
  fixedTimeStep: 1 / 60,
  rngSeed: 'studio-default',
  isCloudRendering: false,
  bounceAngleRandomization: 0,
  rhythmicDeterministicBounce: true,
  useNumericGridKeys: true,
};
